'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { trackCalculateAutoshipFrequency, trackSetAutoshipFrequency } from '@libs/analytics';
import { getENV, logSentryError, logSentryEvent, useDecisions, useOptimizely } from '@libs/common-next';

import { useCalculateFrequency } from '@/entities/calculate-frequency';
import { useProduct } from '@/entities/product';
import {
    getValues as getPurchaseFormValues,
    saveCustomerFrequencySelection,
    setValue as setPurchaseFormValue,
} from '@/entities/purchase-form';
import { hideSuccess, showSuccess } from '@/shared/notifications';
import { SentryErrors, SentryEvents } from '@/shared/sentry';

import { EMPTY_FORM_VALUES, isInvalidNonEmptyTotalCupsPerDay } from './form.constants';
import { getFormSchema } from './form.schema';
import type { FormFields, InFlightCalculation } from './form.types';
import { getFrequencyUpdatedMessage, mapRecommendedFrequencyToAutoship } from './mapRecommendedFrequency';
import { resolveFallbackFrequency, toVisibleFrequencyRecommendation } from './resolveFallbackFrequency';
import {
    clearAppliedCalculatedFrequency,
    getAppliedCalculatedFrequency,
    revertAppliedCalculatedFrequency,
    saveAppliedCalculatedFrequency,
} from './revertAppliedCalculatedFrequency';
import { isSameCalculation, toCalculationRequest, toCalculationRequestFromVariables } from './toCalculationRequest';
import { useFrequencyCalculatorDrawerStore } from './useFrequencyCalculatorDrawerStore';

export const useFrequencyCalculatorForm = () => {
    const locale = getENV('NEXT_PUBLIC_LOCALE');
    const { context } = useOptimizely();
    const isDynamicFrequencyEnabled = useDecisions('dyn_as_freq');
    const as_freq_5_7 = useDecisions('as_freq_5_7');
    const { product } = useProduct();
    const calculationResult = useFrequencyCalculatorDrawerStore(state => state.calculationResult);
    const setCalculationResult = useFrequencyCalculatorDrawerStore(state => state.setCalculationResult);
    const clearCalculationResult = useFrequencyCalculatorDrawerStore(state => state.clearCalculationResult);
    const closeDrawer = useFrequencyCalculatorDrawerStore(state => state.close);
    const inFlightRef = useRef<InFlightCalculation | null>(null);
    const [isCalculating, setIsCalculating] = useState(false);
    const sku = product?.sku;
    const previousSkuRef = useRef(sku);

    const setInFlight = useCallback((request: InFlightCalculation | null) => {
        inFlightRef.current = request;
        setIsCalculating(request !== null);
    }, []);

    const isCurrentInFlight = useCallback(
        (variables: { id: string; numberOfPets: number; totalCupsPerDay: number }) => {
            const current = inFlightRef.current;
            return current !== null && isSameCalculation(current, toCalculationRequestFromVariables(variables));
        },
        []
    );

    const { mutate, reset: resetCalculation } = useCalculateFrequency({
        onSuccess: (response, variables) => {
            if (!isCurrentInFlight(variables)) return;

            setInFlight(null);
            setCalculationResult(
                toVisibleFrequencyRecommendation({ source: 'calculated', ...response }, product, as_freq_5_7)
            );
        },
    });

    const formSchema = useMemo(() => getFormSchema(), []);
    const form = useForm<FormFields>({
        defaultValues: EMPTY_FORM_VALUES,
        resolver: zodResolver(formSchema),
        mode: 'onTouched',
    });

    const { handleSubmit, formState, reset, watch, setValue: setFormValue, getValues } = form;

    const abandonInFlight = useCallback(() => {
        setInFlight(null);
        resetCalculation();
        clearCalculationResult();
    }, [clearCalculationResult, resetCalculation, setInFlight]);

    const submitCalculation = useCallback(
        (request: InFlightCalculation) => {
            const current = inFlightRef.current;
            if (current && isSameCalculation(current, request)) return;

            setInFlight(request);
            mutate(
                {
                    locale,
                    id: request.sku,
                    numberOfPets: request.numberOfPets,
                    totalCupsPerDay: request.totalCupsPerDay,
                    asFreq57: as_freq_5_7,
                },
                {
                    onError: () => {
                        const current = inFlightRef.current;
                        if (!current || !isSameCalculation(current, request)) return;
                        setInFlight(null);
                        setCalculationResult(resolveFallbackFrequency(product, as_freq_5_7));
                    },
                }
            );
        },
        [as_freq_5_7, locale, mutate, product, setCalculationResult, setInFlight]
    );

    const setValue = useCallback<typeof setFormValue>(
        (name, value, options) => {
            const isInputField = name === 'numberOfPets' || name === 'totalCupsPerDay';
            const hasChanged = isInputField && getValues(name) !== value;
            const wasCalculating = inFlightRef.current !== null;

            if (
                hasChanged &&
                !wasCalculating &&
                useFrequencyCalculatorDrawerStore.getState().calculationResult !== null
            ) {
                clearCalculationResult();
            }

            setFormValue(name, value, options);

            if (!hasChanged || !wasCalculating) return;

            const request = toCalculationRequest(getValues(), sku);
            if (request) {
                submitCalculation(request);
                return;
            }

            abandonInFlight();
        },
        [abandonInFlight, clearCalculationResult, getValues, setFormValue, sku, submitCalculation]
    );

    const onSubmit = (values: FormFields) => {
        const request = toCalculationRequest(values, sku);
        if (!request) return;

        trackCalculateAutoshipFrequency({
            totalPets: request.numberOfPets,
            totalCupsFood: request.totalCupsPerDay,
        });

        submitCalculation(request);
    };

    const handleCalculate = handleSubmit(onSubmit);

    const [fieldResetKey, setFieldResetKey] = useState(0);

    const handleFormReset = useCallback(() => {
        abandonInFlight();
        reset(EMPTY_FORM_VALUES);
        setFieldResetKey(key => key + 1);
    }, [abandonInFlight, reset]);

    const handleApplySuggestedFrequency = useCallback(() => {
        if (!calculationResult) return;

        const { numberOfPets, totalCupsPerDay } = getValues();
        const extra = {
            locale,
            sku: sku ?? 'unknown',
            numberOfPets: numberOfPets ?? 'unknown',
            totalCupsPerDay: totalCupsPerDay ?? 'unknown',
            recommendedAutoshipFrequency: String(calculationResult.recommendedAutoshipFrequency),
            frequencyUnit: calculationResult.frequencyUnit,
        };

        try {
            const originalAutoshipUnit = getPurchaseFormValues('autoshipUnit');
            const visibleRecommendation = toVisibleFrequencyRecommendation(calculationResult, product, as_freq_5_7);
            const mappedFrequency = mapRecommendedFrequencyToAutoship(visibleRecommendation);
            const originalAutoshipFrequency = getPurchaseFormValues('autoshipFrequency');

            trackSetAutoshipFrequency({
                totalPets: numberOfPets ? Number(numberOfPets) : null,
                totalCupsFood: totalCupsPerDay ? Number(totalCupsPerDay) : null,
                subscriptionFrequency: visibleRecommendation.recommendedAutoshipFrequency,
                subscriptionFrequencyUnit: visibleRecommendation.frequencyUnit,
            });
            context?.trackEvent('_optex_US-CA_set-frequency-cta_FXE_pdp_s_1080');

            setPurchaseFormValue('autoshipFrequency', mappedFrequency.autoshipFrequency);
            setPurchaseFormValue('autoshipUnit', mappedFrequency.autoshipUnit);

            if (sku) {
                if (isDynamicFrequencyEnabled) {
                    saveCustomerFrequencySelection(sku, {
                        autoshipFrequency: mappedFrequency.autoshipFrequency,
                        autoshipUnit: mappedFrequency.autoshipUnit,
                    });
                }
                saveAppliedCalculatedFrequency({
                    sku,
                    autoshipFrequency: mappedFrequency.autoshipFrequency,
                    autoshipUnit: mappedFrequency.autoshipUnit,
                    originalAutoshipFrequency,
                    originalAutoshipUnit,
                });
            }
            useFrequencyCalculatorDrawerStore.getState().setAppliedFrequency({
                sku,
                autoshipFrequency: mappedFrequency.autoshipFrequency,
                autoshipUnit: mappedFrequency.autoshipUnit,
                source: visibleRecommendation.source,
            });
            handleFormReset();
            closeDrawer();
            showSuccess({
                title: '',
                message: getFrequencyUpdatedMessage(mappedFrequency),
            });
            logSentryEvent('Set autoship frequency', {
                sentryContext: SentryEvents.SET_AUTOSHIP_FREQUENCY,
                tags: {
                    component: 'FrequencyCalculator',
                    severity: 'INFO',
                },
                extra,
            });
        } catch (error) {
            logSentryError(error, {
                ...SentryErrors.FrequencyCalculator.SetAutoshipFrequencyFailed,
                extra: {
                    ...SentryErrors.FrequencyCalculator.SetAutoshipFrequencyFailed.extra,
                    ...extra,
                },
            });
        }
    }, [
        as_freq_5_7,
        calculationResult,
        closeDrawer,
        context,
        getValues,
        handleFormReset,
        isDynamicFrequencyEnabled,
        locale,
        product,
        sku,
    ]);

    useEffect(() => {
        const applied = getAppliedCalculatedFrequency();
        const skuChanged = previousSkuRef.current !== sku;
        const previousSku = previousSkuRef.current;

        if (skuChanged) {
            previousSkuRef.current = sku;
            if (previousSku) {
                hideSuccess();
                abandonInFlight();
                useFrequencyCalculatorDrawerStore.getState().clearAppliedFrequency();
            }
        }

        if (applied && applied.sku !== sku) {
            hideSuccess();
            abandonInFlight();
            // The purchase form re-resolves frequency for the new SKU; restoring here can overwrite it.
            if (isDynamicFrequencyEnabled) {
                clearAppliedCalculatedFrequency();
            } else {
                revertAppliedCalculatedFrequency();
            }
            useFrequencyCalculatorDrawerStore.getState().clearAppliedFrequency();
        }
    }, [abandonInFlight, isDynamicFrequencyEnabled, sku]);

    useEffect(() => {
        return () => {
            inFlightRef.current = null;
            resetCalculation();
            useFrequencyCalculatorDrawerStore.getState().clearAppliedFrequency();
        };
    }, [resetCalculation]);

    useEffect(() => {
        return () => {
            hideSuccess();
            useFrequencyCalculatorDrawerStore.getState().clearCalculationResult();
            useFrequencyCalculatorDrawerStore.getState().clearAppliedFrequency();
            revertAppliedCalculatedFrequency();
        };
    }, []);

    const canReset = formState.isDirty || calculationResult !== null;
    const totalCupsPerDayValue = watch('totalCupsPerDay');
    const isCalculateDisabled = isInvalidNonEmptyTotalCupsPerDay(totalCupsPerDayValue) || !sku;

    return {
        ...form,
        setValue,
        calculationResult,
        handleCalculate,
        handleApplySuggestedFrequency,
        handleFormReset,
        fieldResetKey,
        canReset,
        isCalculateDisabled,
        isCalculating,
    };
};
